<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    endAt: { type: Date, required: true },
    isOpen: { type: Boolean, default: false }
});

const emit = defineEmits(['show-more', 'join-now']);

function padTwoDigits(n) {
    return String(n).padStart(2, '0');
}

const endTimestamp = computed(() => {
    const t = Date.parse(props.endAt);

    return Number.isFinite(t) ? t : Date.now();
});

const now = ref(Date.now());
let timerId = null;
onMounted(() => {
    timerId = window.setInterval(() => {
        now.value = Date.now();
    }, 1000);
});
onBeforeUnmount(() => {
    if (timerId) window.clearInterval(timerId);
});

const countdown = computed(() => {
    const ms = Math.max(endTimestamp.value - now.value, 0);
    const s = Math.floor(ms / 1000);

    return {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60
    };
});

const units = computed(() => [
    { value: countdown.value.days, label: 'TAGE' },
    { value: countdown.value.hours, label: 'STUNDEN' },
    { value: countdown.value.minutes, label: 'MINUTEN' },
    { value: countdown.value.seconds, label: 'SEKUNDEN' }
]);
</script>

<template>
    <div class="flex flex-col xl:flex-row items-center gap-10 xl:gap-[86px]">
        <!-- ── Linke Spalte ── -->
        <div class="flex flex-col gap-5 w-full xl:w-[666px] shrink-0">
            <!-- Headline + Subtitle -->
            <div class="flex flex-col">
                <img :src="$asset('/hkcm/images/wheelTitle.png', 'public')" alt="Das HKCM-Glücksrad" class="wheel-title-img w-auto max-w-[360px] object-contain relative z-[1]" />
                <h2 class="relative z-10 text-3xl xl:text-[50px] xl:leading-[55px] font-semibold">
                    <span class="text-[#DCC149]">Jeder Dreh</span>
                    <span class="text-[#E8E8E8]"> ein Gewinn!</span>
                </h2>
                <div class="relative">
                    <div class="text-haze-bg absolute pointer-events-none" aria-hidden="true" />
                    <p class="relative text-base xl:text-[20px] xl:leading-[28px] text-[#C3C3C3] tracking-[0.231px]">
                        <strong class="text-[#E8E8E8]">Traumreisen</strong> sind für Sie in weiter Ferne? Ein neues <strong class="text-[#E8E8E8]">Fahrzeug</strong> ist
                        unerschwinglich? <strong class="text-[#E8E8E8]">Geldreserven</strong> werden für den Alltag aufgebraucht?<br />
                        HKCM bringt Sie ein Stück näher ans Glück!<br />
                        Vom 28. Mai bis 07. Juni 2026 erhält jede Bestellung <strong class="text-[#DCC149]">ab 129 €</strong> garantiert einen exklusiven Glücksrad-Preis. Und das
                        Beste: Jeder Teilnehmer nimmt automatisch an der Verlosung unserer <strong class="text-[#E8E8E8]">exklusiven Hauptpreise*</strong> im Wert von
                        <strong class="text-[#E8E8E8]">über 30.000 €</strong> teil.
                    </p>
                </div>
            </div>

            <!-- Fußnote -->
            <p class="text-sm xl:text-[20px] xl:leading-[28px] font-medium text-[#C3C3C3] tracking-[0.231px]">*Hauptpreis-Auslosung am Ende der Kampagne</p>

            <!-- Countdown-Label -->
            <p class="text-xl xl:text-[26px] xl:leading-[32px] font-semibold text-[#DCC149] tracking-[-0.576px]">Diese Aktion endet in:</p>

            <!-- Countdown-Boxen -->
            <div class="flex flex-row gap-3 xl:gap-4">
                <div
                    v-for="unit in units"
                    :key="unit.label"
                    class="flex flex-col items-center justify-center flex-1 xl:w-[124px] xl:flex-none h-[90px] bg-white border border-[#D8D8D8] rounded-[12px] px-2 xl:px-8"
                >
                    <span class="text-2xl xl:text-[30px] xl:leading-[37px] font-semibold text-black tabular-nums tracking-[-0.0014em]">
                        {{ padTwoDigits(unit.value) }}
                    </span>
                    <span class="text-[11px] xl:text-[16px] xl:leading-[15px] font-medium text-[#86868B] tracking-[0.231px] mt-[10px]">
                        {{ unit.label }}
                    </span>
                </div>
            </div>

            <!-- Buttons -->
            <div class="flex flex-row flex-wrap gap-4">
                <button
                    class="flex items-center justify-center h-14 px-7 bg-[#0066CC] border border-[#0071E3] rounded-full text-white font-semibold text-lg whitespace-nowrap transition hover:brightness-110 active:scale-[.99]"
                    @click="emit('join-now')"
                >
                    Jetzt mitmachen
                </button>
                <button
                    class="group inline-flex items-center gap-3 rounded-full px-6 py-3 bg-[#0b1428] text-white ring-2 ring-amber-400/50 border border-white/5 shadow-[0_10px_30px_rgba(0,0,0,.45)] hover:ring-amber-300/70 hover:brightness-[1.06] active:scale-[.99] transition"
                    @click="emit('show-more')"
                >
                    <span class="text-sm font-semibold">{{ props.isOpen ? 'Weniger anzeigen' : 'Mehr anzeigen' }}</span>
                    <svg class="h-4 w-4 transition-transform duration-300 text-amber-300" :class="props.isOpen ? 'rotate-180' : ''" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fill-rule="evenodd"
                            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        </div>

        <!-- ── Rechte Spalte: Hauptpreise + Rad ── -->
        <div class="hidden xl:flex flex-row items-center gap-4 shrink-0">
            <!-- Rad + Hauptpreise overlay -->
            <div class="relative flex items-center justify-center" style="width: min(700px, 45vw); height: min(700px, 45vw)">
                <img :src="$asset('/hkcm/images/wheelofFortuneFilledAndPrices.png', 'public')" alt="Glücksrad" class="w-full h-full object-contain" style="filter: drop-shadow(0px 4px 16px rgba(0, 0, 0, 0.57))" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-haze-bg {
    top: -30px;
    bottom: -120px;
    left: -160px;
    right: -160px;
    border-radius: 50%;
    background: radial-gradient(ellipse at center, rgba(3, 8, 30, 0.65) 0%, rgba(3, 8, 30, 0.35) 45%, transparent 72%);
}

.wheel-title-img {
    filter: drop-shadow(0 0 16px rgba(220, 193, 73, 0.55));
    transition: filter 0.3s ease;
}
.wheel-title-img:hover {
    filter: drop-shadow(0 0 32px rgba(220, 193, 73, 0.95));
}
</style>
